import React, {useEffect, useRef} from "react";
import {GetMessageCodeText} from "../app-components/outputs/MessageCodes";
import {CheckOperations, ComparisonOperations} from "../app-components/run-project/Operations";
import {v4} from "uuid";
import HecRasHelper from "../helpers/HecRasHelper";
import {Layers} from "../app-components/map-plugins/LayerSelect";
import {type} from "plotly.js/src/components/errorbars/attributes";
import { debounce } from 'lodash';
export const AppActions = {
    SET_APP_CONTEXT: 'SET_APP_CONTEXT',
    SET_PROJECT: 'SET_PROJECT',
    SET_PROJECTS: 'SET_PROJECTs',
    REMOVE_PROJECT: 'REMOVE_PROJECT',
    SET_HISTORY: 'SET_HISTORY',
    SET_HISTORY_LIST: 'SET_HISTORY_LIST',
    SET_SELECTED_HISTORY_ID : 'SET_SELECTED_HISTORY_ID',
    PUSH_OPERATION_MESSAGE: 'PUSH_OPERATION_MESSAGE',
    SET_TAB: 'SET_TAB',
    SET_SELECTED_OPERATION: 'SET_SELECTED_OPERATION',
    SET_SELECTED_ERROR_ID: 'SET_SELECTED_ERROR_ID',
    SET_SELECTED_RS: 'SET_SELECTED_RS',
    SET_MESSAGE_TOOLBAR_ITEM: 'SET_MESSAGE_TOOLBAR_ITEM',
    SET_PAGE_SETTINGS: 'SET_PAGE_SETTINGS',
    SET_MAP_CONTEXT: 'SET_MAP_CONTEXT',
    SET_MAP_ZOOM: 'SET_MAP_ZOOM',
    SET_MAP_CENTER: 'SET_MAP_CENTER',
    SET_MAP_TYPE: 'SET_MAP_TYPE',
    SET_PROJECT_SELECTION: 'SET_PROJECT_SELECTION',
    CLEAR: 'CLEAR'
}

const charArray = () => {
    const chars = []
    for(let i=65; i<=90; i++) {
        chars.push(String.fromCharCode(i))
    }

    return chars
}
export const ModelNames = charArray()?.map(x => `Model ${x}`)

export const DefaultPageSettings = {
    filter: {
        riverStations: [],
        type: 'clear', // filter-color-clear
        us: '',
        ds: ''
    },
    sort: 'ds'
}

export const Bounding = {
    northWest: 250,
    northEast: 250,
    southWest: 250,
    southEast: 250,
    showBounding: false
}

export const FlowArrow = {
    flow_count: 5,
    flow_size: 15,
    flow_change_size: 50,
    boundary_size: 50
}

export const TerrainLayer = {
    resolution_size: 256,
    opacity_size: 1,
    gray_opacity_size: 0.5,
    gray_z_factor: 1,
}

export const LMASettings = {
    id: null,
    gageRS: null,
    minBankElev: null,
    yr100WSEL: null,
    yr500WSEL: null,
    maxHighChord: null,
    minTargetElev: null,
    maxTargetElev: null,
    targetElevInterval: 0.5,
    numberOfTargetProfiles: null,
}

export const LMAData = {
    runKey: null,
    originalValues: {
        ...LMASettings,
    },
    editedValues: {
        ...LMASettings,
    },
    flowTable: [],
    numberOfProfileSearch: 1000,
    flowData: {},
    prepared: false
};

const DefaultValue = {
    page: {...DefaultPageSettings},
    filter: {
        riverStations: [],
        type: 'clear',
        us: '',
        ds: ''
    },
    filterType: '',
    projects: [],
    history: [],
    selectedHistoryId: '',
    selectedTab: 'Message',
    selectedOperation: null,
    previousOperation: null,
    selectedErrorId: null,
    selectedRS: null,
    plot: {},
    selectedMapType: 'base',
    map: {
        base: {
            referenceLayers: [],
            projectLayers: [],
            selectedLayers: {
                model: [...Layers.filter(x => !['Flow Change Location', 'Flow Path', 'Boundary Condition'].includes(x))],
                reference: [],
                project: []
            },
            bounding: {...Bounding},
            flowArrow: {
                ...FlowArrow
            },
            terrainLayers: {
                ...TerrainLayer
            },
            zoom: 18,
        },
        compare: {
            referenceLayers: [],
            projectLayers: [],
            selectedLayers: {
                model: [...Layers.filter(x => !['Flow Change Location', 'Flow Path', 'Boundary Condition'].includes(x))],
                reference: [],
                project: []
            },
            bounding: {...Bounding},
            flowArrow: {
                ...FlowArrow
            },
            terrainLayers: {
                ...TerrainLayer
            },
            zoom: 18,
        }
    },
    selection: {
        base: {},
        compare: {}
    },
    toolbarItem: null,
    updateKey: v4()
}

const ProjectContext = React.createContext(JSON.parse(JSON.stringify(DefaultValue)));


const processProject = (action, state) => {
    if(!action?.data?.project?.id)
        action.data.project.id = action?.data?.project?.information?.id;

    const _state = {...state}
    if(action?.data?.map) {
        _state.map[action?.data?.map?.type].update = action.data.map[action?.data?.map?.type].update
    }

    const id = action?.data?.project?.information?.id;
    const existIndex = _state?.projects?.findIndex(x => x.information?.id === id)

    if(existIndex >= 0) {
        _state.projects[existIndex] = {...action.data.project}

        // remove plan or project selection
        if(_state.projects[existIndex].projectFiles?.length === 0) {
            _state.projects = _state?.projects?.filter(x => x.information?.id !== id)
        }
        else {
            if(state?.selection?.base?.project?.information?.id === _state.projects[existIndex]?.information?.id) {
                const findPlan = _state.projects[existIndex]?.projectFiles?.find(x => x.id === state?.selection?.base?.plan?.id)
                if(!findPlan)
                    state.selection.base = {}
            }
            if(state?.selection?.compare?.project?.information?.id === _state.projects[existIndex]?.information?.id) {
                const findPlan = _state.projects[existIndex]?.projectFiles?.find(x => x.id === state?.selection?.compare?.plan?.id)
                if(!findPlan)
                    state.selection.compare = {}
            }
        }
    }
    else {
        const allModels = [...ModelNames]
        const existModels = _state.projects.map(prj => prj.modelType)
        const notExistModels = allModels.filter(x => !existModels.includes(x))

        _state.projects.push({
            ...action.data.project,
            modelType: notExistModels[0]
        })
    }

    return {
        ..._state
    }
}

const applyFilterToHistory = (history, filter, selectedOperation) => {
    if (!history || !filter) return history
    const h = JSON.parse(JSON.stringify(history))
    h.operations = HecRasHelper.filterResult({
        operations: h.operations,
        filter
    })
    h.operations = HecRasHelper.sortOperationsResults({
        direction: h.page?.sort,
        operations: h.operations
    })
    h.updateKey = v4()
    return h
}

function projectReducer(state, action) {
    switch (action.type) {
        case AppActions.CLEAR: {
            return {
                ...JSON.parse(JSON.stringify(DefaultValue))
            }
        }
        case AppActions.SET_APP_CONTEXT: {
            return {
                ...action?.data?.context
            }
        }
        case AppActions.SET_PROJECT: {
            return processProject(action, state)
        }
        case AppActions.SET_PROJECTS: {
            let _state = {...state}
            for (const project of (action?.data?.projects || [])) {
                _state = processProject({data: {project: {...project}}}, state)
            }
            return {
                ..._state
            }
        }
        case AppActions.REMOVE_PROJECT: {
            state.projects = state.projects.filter(x => x.information?.id !== action?.data?.id)
            if(state?.selection?.base?.project?.information?.id === action?.data?.id) {
                state.selection.base = {}
            }
            if(state?.selection?.compare?.project?.information?.id === action?.data?.id) {
                state.selection.base = {}
            }

            state.history = state.history.filter(x => {
                if(x.selection) {
                    if(x.selection.base?.project?.information?.id === action?.data?.id)
                        return false

                    if(x.selection.compare?.project?.information?.id === action?.data?.id)
                        return false
                }

                return true
            })

            if(state.projects.length === 0) {
                state.filter = {
                    riverStations: [],
                    type: 'clear',
                    us: '',
                    ds: ''
                }
            }

            return {
                ...state
            }
        }
        case AppActions.SET_HISTORY: {
            const history = action.data.history.map((x, index) => {
                if(!x.page)
                    x.page = JSON.parse(JSON.stringify(DefaultPageSettings))

                return applyFilterToHistory(x, state.filter)
            })

            return {
                ...state,
                history: [...history]
            }
        }
        case AppActions.PUSH_OPERATION_MESSAGE: {
            const _state = {...state}
            const historyIndex = _state.history.findIndex(x => x.id === action.data?.id)
            if(historyIndex >= 0) {
                _state.history[historyIndex] = applyFilterToHistory(_state.history[historyIndex], _state.filter)
            }
            return {
                ..._state,
                selectedHistoryId: action.data?.id,
                previousOperation: {...(state.selectedOperation || {})},
                selectedOperation: action.data?.operation,
                selectedTab: action.data?.tab
            }
        }
        case AppActions.SET_SELECTED_HISTORY_ID: {
            const _state = {...state}
            const historyIndex = _state.history.findIndex(x => x.id === action.data?.id)
            if(historyIndex >= 0) {
                _state.history[historyIndex] = applyFilterToHistory(_state.history[historyIndex], _state.filter)
            }

            if(action.data.operation) {
                return {
                    ..._state,
                    selectedHistoryId: action.data?.id,
                    previousOperation: {...(state.selectedOperation || {})},
                    selectedOperation: action.data?.operation
                }
            }

            return {
                ..._state,
                selectedHistoryId: action.data?.id
            }
        }
        case AppActions.SET_TAB: {
            return {
                ...state,
                selectedTab: action.data?.tab
            }
        }
        case AppActions.SET_SELECTED_OPERATION: {
            return {
                ...state,
                previousOperation: {...(state.selectedOperation || {})},
                selectedOperation: action.data?.item
            }
        }
        case AppActions.SET_PAGE_SETTINGS: {
            const _state = {...state}

            // Update global filter
            if(action.data.page?.filter) {
                _state.filter = {
                    ...(_state.filter || {}),
                    ...(action.data.page.filter)
                }

                if(_state.filter.type === 'clear' && (_state.filter.us || _state.filter.ds)) {
                    _state.filter.type = 'filter'
                }
            }

            const historyIndex = _state.history.findIndex(x => x.id === _state.selectedHistoryId)

            if(historyIndex >= 0) {
                // Deep copy the history item to avoid reference issues
                const history = JSON.parse(JSON.stringify(_state.history[historyIndex]))

                const { filter: _filter, ...pageWithoutFilter } = action.data.page || {}
                history.page = {
                    ...history.page,
                    ...pageWithoutFilter
                }

                history.updateKey = v4()

                history.operations = HecRasHelper.filterResult({
                    operations: history.operations,
                    filter: _state.filter
                })

                history.operations = HecRasHelper.sortOperationsResults({
                    direction: history.page.sort,
                    operations: history.operations
                })

                for (const operation of history.operations) {
                    const selOperation = operation?.children?.find(x => x?.key === _state?.selectedOperation?.key && x?.key_text === _state?.selectedOperation?.key_text)
                    if(selOperation) {
                        _state.selectedOperation = {...selOperation}
                    }
                }

                // Update the specific history item in the array
                _state.history = [
                    ..._state.history.slice(0, historyIndex),
                    history,
                    ..._state.history.slice(historyIndex + 1)
                ]
            }

            return {
                ..._state
            }
        }
        case AppActions.SET_SELECTED_ERROR_ID: {
            return {
                ...state,
                selectedErrorId: action.data?.errorId
            }
        }
        case AppActions.SET_SELECTED_RS: {
            return {
                ...state,
                selectedRS: action.data?.rs
            }
        }
        case AppActions.SET_MESSAGE_TOOLBAR_ITEM: {
            return {
                ...state,
                toolbarItem: action.data?.toolbar
            }
        }
        case AppActions.SET_MAP_CONTEXT: {
            return {
                ...state,
                map: {...action.data?.map}
            }
        }
        case AppActions.SET_MAP_TYPE: {
            return {
                ...state,
                selectedMapType: action.data?.mapType || 'base'
            }
        }
        case AppActions.SET_MAP_ZOOM: {
            const type = action?.data?.type
            return {
                ...state,
                map: {
                    ...state.map,
                    [type]: {
                        ...state.map[type],
                        zoom: action.data?.zoom || 18
                    }
                }
            }
        }
        case AppActions.SET_PROJECT_SELECTION: {
            return {
                ...state,
                selection: {...action.data?.selection}
            }
        }
        default: {
            return {...state}
        }
    }
}

function AppProvider({children}) {
    const [project, dispatch] = React.useReducer(projectReducer, JSON.parse(JSON.stringify(DefaultValue)))

    const value = {project, dispatch}

    return (
        <ProjectContext.Provider value={value}>
            {children}
        </ProjectContext.Provider>
    )
}

function useAppContext() {
    const context = React.useContext(ProjectContext)

    if(!context)
        throw new Error('Project Provider Error')

    const {project: appContext, dispatch} = context;

    const dispatchProject = ({type, data}) => {
        dispatch({
            type,
            data: {...data}
        })
    }

    const getSelectedHistory = () => {
        return appContext?.history?.find(x => x?.id === appContext?.selectedHistoryId) || {operations: []}
    }

    const getOperationByErrorId = () => {
        const selectedOperation = (appContext?.selectedOperation || {})
        return {
            ...selectedOperation,
            data: {
                ...(selectedOperation?.data || {}),
                result: selectedOperation?.data?.result?.filter(x => GetMessageCodeText(selectedOperation?.key, x.error_id) === appContext?.selectedErrorId) || []
            }
        }
    }

    const getResultListByErrorId = () => {
        const selectedOperation = (appContext?.selectedOperation || {})
        return selectedOperation?.data?.result?.find(x => GetMessageCodeText(selectedOperation?.key, x.error_id) === appContext?.selectedErrorId)
    }

    const getCSListByErrorId = () => {
        return getResultListByErrorId()?.data?.map(r => r.cross_section)
    }

    const getMessageCodeList = () => {
        const selectedOperation = (appContext?.selectedOperation || {})
        return selectedOperation?.data?.result?.map(d => GetMessageCodeText(selectedOperation?.key, d?.error_id))
    }

    const getSelectedRS = () => {
        const rs = appContext?.selectedRS?.toString()
            ?.replace('Culv', '')
            ?.replace('BR', '')
            ?.replace('(Sur)', '')
            ?.trim()

        const find = getResultListByErrorId()?.data?.find(x => x.cross_section === rs || x.cross_section_value === rs || x.cross_section === appContext?.selectedRS || x.cross_section_value === appContext?.selectedRS)

        if(find)
            return find

        return {cross_section: rs}
    }

    const getOperationItemConfig = () => {
        for (const checkOperation of [...CheckOperations, ...ComparisonOperations]) {
            for (const child of checkOperation.children) {
                if(child.key === appContext?.selectedOperation?.key) {
                    return child.ui
                }
            }
        }
    }

    const getHistorySorted = () => {
        const _history = [...(appContext?.history || [])]
        _history.sort(function(a, b) {
            // const isimA = a?.selection?.base?.project?.information?.title?.toUpperCase(); // Büyük/küçük harf duyarlı olmayacak şekilde isimleri karşılaştırır
            // const isimB = b?.selection?.base?.project?.information?.title?.toUpperCase();
            // if (isimA < isimB) {
            //     return -1;
            // }
            // if (isimA > isimB) {
            //     return 1;
            // }
            // İsimler aynı ise tarihlerine göre sıralama
            return new Date(a.date) - new Date(b.date);
        });

        return _history
    }

    const getProjectLayers = (type, _selection) => {
        const selection = _selection || getSelectedHistory()?.selection
        if(selection) {
            const _project = appContext?.projects?.find(x => x.id === selection[type]?.project?.id)
            if(_project)
                return _project.shapeFiles || []
        }

        return [];
    }

    const getMapData = (type) => {
        if(type === 'base')
            return getSelectedHistory()?.mapBase

        return getSelectedHistory()?.mapCompare
    }

    const getRiverBoundingCoords = (type, _mapData) => {
        const mapData = _mapData || getMapData(type)
        if(!mapData) {
            return []
        }

        const us = mapData?.cross_sections[0]
        const ds = mapData?.cross_sections[mapData?.cross_sections?.length-1]

        const reach_xy = mapData?.reach_xy
        const reach_coords = []
        for(let i=0; i<reach_xy.length; i++) {
            if(i >= us?.river_index && i <= ds?.river_index) {
                reach_coords.push(reach_xy[i])
            }
        }

        return reach_coords
    }

    const getCSList = () => {
        if(getSelectedHistory()?.summary?.compare) {
            return getSelectedHistory()?.summary?.compare?.cs_list || []
        }

        if(getSelectedHistory()?.summary?.base) {
            return getSelectedHistory()?.summary?.base?.cs_list || []
        }

        return []
    }

    const hasGeoReference = () => {
        // if(runType === 'Check') {
        //     return HecRasHelper.hasGeoReferenced(selection.base.plan?.geometry?.text)
        // }
        // else {
        //     return HecRasHelper.hasGeoReferenced(selection.base.plan?.geometry?.text) && HecRasHelper.isLibraryPlan(selection.compare.plan?.geometry?.text)
        // }
    }

    const getSelectedProject =(type, projects) => {
        return (projects || appContext?.projects)?.find(
            (x) => x.id === appContext?.selection?.[type]?.project?.information?.id
        );
    }

    const getSelectedPlan = (type, project) => {
        const _project = project || getSelectedProject(type);
        return _project?.projectFiles?.find(
            (x) => x?.id === appContext?.selection?.[type]?.plan?.id
        );
    };

    const getLMAOfSelectedPlan = (type) => {
        return getSelectedPlan(type)?.LMA || {...LMAData}
    }

    const updateLMAOfSelectedPlan = ({ type, data }) => {
        const _project = getSelectedProject(type);
        const _plan = getSelectedPlan(type, _project);

        if (_plan) {
            _plan.LMA = {
                ...getLMAOfSelectedPlan(type),
                ...data,
                id: v4(),
            };

            dispatchProject({
                type: AppActions.SET_PROJECT,
                data: {
                    project: {
                        ..._project,
                    },
                },
            });
        }
    };

    const resetShapeFilters = (filterType) => {
        if(appContext?.projects?.length === 0)
            return

        for (const project of appContext?.projects) {
            for (const shapeFile of project?.shapeFiles) {
                if(shapeFile.hasSurveyData) {
                    if(filterType === 'message') {
                        shapeFile.filters = {...shapeFile.filtersMessage};
                    }
                    else if(filterType === 'automation') {
                        shapeFile.filters = {...shapeFile.filtersSurveyAutomation};
                    }
                }
            }
        }

        dispatchProject({
            type: AppActions.SET_APP_CONTEXT,
            data: {
                context: appContext
            }
        })
    }

    return {
        appContext: appContext,
        filter: appContext?.filter,
        selectedOperation: appContext?.selectedOperation,
        selectedErrorId: appContext?.selectedErrorId,
        selectedRS: appContext?.selectedRS,
        selectedTab: appContext?.selectedTab,
        toolbarItem: appContext?.toolbarItem,
        selectedHistoryId: appContext?.selectedHistoryId,
        previousOperation: appContext?.previousOperation,
        mapContext: appContext?.map,
        selection: appContext?.selection,
        getProjectLayers,
        dispatchProject,
        getSelectedHistory,
        getOperationByErrorId,
        getCSListByErrorId,
        getMessageCodeList,
        getSelectedRS,
        getOperationItemConfig,
        getHistorySorted,
        getMapData,
        getRiverBoundingCoords,
        getCSList,
        getSelectedProject,
        getSelectedPlan,
        getLMAOfSelectedPlan,
        updateLMAOfSelectedPlan,
        resetShapeFilters,
    };
}

export {AppProvider, useAppContext}